import { ReactNode, useState } from 'react';
import styled from 'styled-components';

import { Caret } from 'shared/icons/Caret';
import { DESKTOP_BANNER_HEIGHT } from 'banner/enums/constants';
import { DESKTOP_HEADER_HEIGHT } from 'layout/header/enums/constants';
import { hideScrollbars } from 'styles/utils/hideScrollbars';
import { TAB_INITIAL_HEIGHT, TAB_INITIAL_HEIGHT_MOBILE } from 'homepage/styles/constants';
import { useIsBannerVisible } from 'shared/hooks/useIsBannerVisible';
import { useStoreSelector } from 'store/hooks/storeHooks';
import { variableFontRegular } from 'styles/theme/text';
import { useFeatureFlag } from 'shared/hooks/useFeatureFlag';
import { FeatureFlags } from 'featureFlags/constants';

interface IGridTab {
  backgroundColor?: string;
  children?: ReactNode;
  collectionName: string;
  setShowBrandStoryOverlay: (value: any) => void;
  setShowTab: (value: boolean) => void;
  showBrandStoryOverlay: boolean;
  showTab?: boolean;
}

export const GridTab = ({
  backgroundColor,
  children,
  collectionName,
  setShowBrandStoryOverlay,
  setShowTab,
  showBrandStoryOverlay,
  showTab,
}: IGridTab) => {
  const [hoveredOnClosedTab, setHoveredOnClosedTab] = useState(false);
  const [debouncedState, setDebouncedState] = useState(null);
  const isNewSearchPageEnabled = useFeatureFlag(FeatureFlags.TEMP_WEB_ENABLE_SEARCH_MICROFRONTEND);

  const { currentTheme } = useStoreSelector((state) => state.pageLayout);

  const isBannerVisible = useIsBannerVisible();

  const handleTabClick = () => {
    setShowBrandStoryOverlay((currentOverlayStatus) => !currentOverlayStatus);
    setShowTab(false);
  };

  const handleExpandTab = () => {
    if (!hoveredOnClosedTab) {
      setHoveredOnClosedTab(true);
    } else {
      clearTimeout(debouncedState);
      setShowTab(true);
    }
  };

  const handleReCollapseTab = () => {
    if (showTab && showBrandStoryOverlay && hoveredOnClosedTab) {
      setDebouncedState(
        setTimeout(() => {
          setShowTab(false);
        }, 200),
      );
    }
  };

  return (
    <GridTab.Container
      $showTab={showTab}
      $isBannerVisible={isBannerVisible}
      $isTabOpen={!showBrandStoryOverlay}
      onMouseEnter={handleExpandTab}
      onMouseLeave={handleReCollapseTab}
    >
      <GridTab.HeaderContainer
        $backgroundColor={backgroundColor}
        $isBannerVisible={isBannerVisible}
        onClick={handleTabClick}
      >
        <GridTab.CollectionName>{collectionName}</GridTab.CollectionName>
        <GridTab.Caret
          color={currentTheme?.borderColor}
          direction={!showBrandStoryOverlay ? 'down' : 'up'}
          data-qa="grid_tab_arrow"
          size={8}
          strokeWidth={2}
        />
      </GridTab.HeaderContainer>

      {!isNewSearchPageEnabled && (
        <GridTab.GridTop $backgroundColor={backgroundColor} $isBannerVisible={isBannerVisible} />
      )}

      <GridTab.ContentContainer
        $backgroundColor={backgroundColor}
        $isBannerVisible={isBannerVisible}
        $isTabOpen={!showBrandStoryOverlay}
      >
        {children}
      </GridTab.ContentContainer>
      <GridTab.GridBottom $backgroundColor={backgroundColor} />
    </GridTab.Container>
  );
};

GridTab.Container = styled.div<{
  $isBannerVisible: boolean;
  $isTabOpen: boolean;
  $showTab: boolean;
}>`
  background-color: transparent;
  max-height: calc(100vh - 50px);
  height: calc(100vh - 50px);
  cursor: pointer;
  align-items: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  position: fixed;
  width: 100%;
  z-index: 400;
  bottom: ${({ $isTabOpen, $showTab, $isBannerVisible }) => {
    if ($isTabOpen) {
      return `0px`;
    }
    if ($showTab) {
      return $isBannerVisible
        ? `calc(-100vh + 100px + ${DESKTOP_BANNER_HEIGHT})`
        : `calc(-100vh + 100px)`;
    }
    return $isBannerVisible
      ? `calc(-100vh - 50px - ${DESKTOP_BANNER_HEIGHT})`
      : `calc(-100vh - 50px)`;
  }};
  transition: all 0.3s ease-in 0s;
  ${hideScrollbars}

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    max-height: ${({ $isBannerVisible }) =>
      $isBannerVisible ? `calc(100vh - 42px - ${DESKTOP_BANNER_HEIGHT})` : `calc(100vh - 42px)`};
    height: ${({ $isBannerVisible }) =>
      $isBannerVisible ? `calc(100vh - 42px - ${DESKTOP_BANNER_HEIGHT})` : `calc(100vh - 42px)`};
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) and (max-width: ${({
      theme,
    }) => theme.breakpoints.mediumLarge}px) {
    bottom: ${({ $isTabOpen, $isBannerVisible }) => !$isBannerVisible && $isTabOpen && '-8px'};
  }
`;

GridTab.HeaderContainer = styled.div<{ $backgroundColor: string; $isBannerVisible: boolean }>`
  align-items: center;
  background-color: ${({ theme, $backgroundColor }) =>
    $backgroundColor || theme.navigation.backgroundColor};
  border-top: 1px solid ${({ theme }) => theme.navigation.borderColor};
  cursor: pointer;
  display: flex;
  height: ${TAB_INITIAL_HEIGHT_MOBILE};
  justify-content: space-between;
  left: 13px;
  position: absolute;
  top: 8px;
  width: calc(100% - 40px);
  z-index: ${({ theme }) => theme.zIndex.brandOverlay};

  ${variableFontRegular({ size: '11.5px', height: '11px' })}

  ${hideScrollbars}

  &::before {
    background-color: ${({ theme, $backgroundColor }) =>
      $backgroundColor || theme.navigation.backgroundColor};
    border-left: 1px solid ${({ theme }) => theme.navigation.borderColor};
    content: '';
    height: 70px;
    left: 0px;
    position: absolute;
    top: -1px;
    transform: rotate(44deg);
    transform-origin: top left;
    width: 42px;
  }

  &::after {
    background-color: ${({ theme, $backgroundColor }) =>
      $backgroundColor || theme.navigation.backgroundColor};
    border-right: 1px solid ${({ theme }) => theme.navigation.borderColor};
    content: '';
    height: 70px;
    position: absolute;
    right: 0px;
    top: -1px;
    transform: rotate(-37.2deg);
    transform-origin: top right;
    width: 48px;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    height: ${TAB_INITIAL_HEIGHT};

    &::before {
      transform: rotate(19deg);
    }

    &::after {
      transform: rotate(-34deg);
    }
  }
`;

GridTab.ContentContainer = styled.div<{
  $backgroundColor: string;
  $isBannerVisible: boolean;
  $isTabOpen: boolean;
}>`
  background-color: ${({ theme, $backgroundColor }) =>
    $backgroundColor || theme.navigation.backgroundColor};
  bottom: ${({ $isTabOpen, $isBannerVisible }) => {
    const bottom = $isTabOpen ? `-24px` : `-54px`;
    if ($isBannerVisible) {
      return `calc(${bottom} - ${DESKTOP_BANNER_HEIGHT})`;
    }

    return bottom;
  }};
  height: 100%;
  overflow: scroll;
  width: 100%;
  position: relative;
  ${hideScrollbars}
`;

GridTab.CollectionName = styled.div`
  ${variableFontRegular({ size: '11.5px', height: '14px' })};

  align-items: center;
  bottom: 0;
  display: flex;
  justify-content: center;
  left: 0px;
  position: absolute;
  text-align: center;
  text-transform: uppercase;
  top: 0;
  right: -20px;
  margin: auto;
  z-index: ${({ theme }) => theme.zIndex.arrowButton};
`;

GridTab.Caret = styled(Caret)`
  position: absolute;
  right: 4px;
  z-index: ${({ theme }) => theme.zIndex.arrowButton};
`;

GridTab.GridTop = styled.div<{ $backgroundColor: string; $isBannerVisible: boolean }>`
  display: none;

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    background-color: ${({ theme, $backgroundColor }) =>
      $backgroundColor || theme.navigation.backgroundColor};
    border-bottom: 1px solid ${({ theme }) => theme.navigation.borderColor};
    display: block;
    height: 27px;
    position: absolute;
    top: ${({ $isBannerVisible }) =>
      $isBannerVisible
        ? `calc(${DESKTOP_HEADER_HEIGHT} + ${DESKTOP_BANNER_HEIGHT})`
        : `${DESKTOP_HEADER_HEIGHT}`};
    width: 75.1%;
    z-index: ${({ theme }) => theme.zIndex.brandPageTab};
  }
`;
GridTab.GridBottom = styled.div<{ $backgroundColor: string }>`
  display: none;

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    background-color: ${({ theme, $backgroundColor }) =>
      $backgroundColor || theme.navigation.backgroundColor};
    bottom: 26px;
    border-top: 1px solid ${({ theme }) => theme.navigation.borderColor};
    position: absolute;
    width: 75%;
    z-index: ${({ theme }) => theme.zIndex.brandPageTab};
  }
`;
